// This is top level component of the whole Listing page
// It renders header of the page and footer of the page if data were loaded correctly
// If error occurs on server it renders error component
// if no error occurs, it renders <ListingPageBody /> component
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import throttle from 'lodash.throttle';
import * as log from 'loglevel';

import { EnvironmentThemeProvider } from '@codex/themes';
import Configuration from '../../../../configuration';
import Header from '../../../../components/Header/Header';
import UiLocale from '../../translations/UILocale';
import FooterContainer from '../FooterContainer/FooterContainer';

import { getPageDataAfterChangingPageUsingBrowserHistory } from '../../reduck/listingPagePages.reduck';
import Error404 from '../ErrorPagesComponents/Error404';
import Error500 from '../ErrorPagesComponents/Error500';
import { updateBrowserInfo } from '../../reduck/listingPageBrowser.reduck';
import { loadAndSetGroupcall } from '../../reduck/configuration.reduck';
import ListingPageBody from '../ListingPageBody/ListingPageBody';
import { fetchSearchListingPageURLBasedOnSearchQuery } from '../../dataFetcher/dataFetcher';

import { ResetCss } from './ResetCss';
import { GlobalStyles } from './GlobalStyles';
import { ListingPageAppWrapper, ListingPageAppGlobals } from './styled';

// TODO: refactor old styles to styled-components
// import old styles from fo-site-ui
require('../../old-css-styles/index.scss');

// TODO: refactor to styled components
require('./MainContainer.scss');

const ListingPageApp = ({
  wasErrorOnServer,
  errorCode,
  initialNavigation,
  isUserLoggedIn,
  itemsInBasket,
  userName,
  updateInfoAboutBrowser,
  loadGroupcallAndSetGroupcallData,
  updatePageAfterChangingPageUsingBrowserHistory
}) => {
  const [initOnClientFinished, setInitOnClientFinished] = useState(false);

  // callback for updating browser info on resize
  const updateInfoAboutBrowserCallback = useCallback(
    throttle(
      () => {
        updateInfoAboutBrowser();
      },
      100,
      { leading: false }
    ),
    [updateInfoAboutBrowser]
  );

  // callback for updating browser history
  const updatePageAfterChangingPageUsingBrowserHistoryCallback = useCallback(
    (e) => {
      if (e.state) {
        updatePageAfterChangingPageUsingBrowserHistory(e);
      }
    },
    [updatePageAfterChangingPageUsingBrowserHistory]
  );

  // This inits the listing page in browser after all data are loaded
  useEffect(() => {
    if (!initOnClientFinished) {
      // this is called only on init of the page

      // update browser info
      updateInfoAboutBrowser();

      // load and set groupcall data
      loadGroupcallAndSetGroupcallData();

      setInitOnClientFinished(true);
    }

    // changes browser info after resize of page
    window.addEventListener('resize', updateInfoAboutBrowserCallback);

    // when user changes page by GUI in the web browser e. g. back or forward button,
    // this loads data for the new page using AJAX calls instead of reloading of the whole page
    window.addEventListener('popstate', updatePageAfterChangingPageUsingBrowserHistoryCallback);

    return () => {
      window.removeEventListener('resize', updateInfoAboutBrowserCallback);
      window.removeEventListener(
        'popstate',
        updatePageAfterChangingPageUsingBrowserHistoryCallback
      );
    };
  }, [
    initOnClientFinished,
    updateInfoAboutBrowserCallback,
    updatePageAfterChangingPageUsingBrowserHistoryCallback
  ]);

  return (
    <>
      <ResetCss />
      <GlobalStyles />
      <ListingPageAppGlobals />
      <EnvironmentThemeProvider>
        <ListingPageAppWrapper
          className={'main-container dc-base-font dc-border-box'}
          data-theme={Configuration.get('app_theme')}
        >
          {initialNavigation && (
            <Header
              searchAction={''}
              isLoggedIn={isUserLoggedIn}
              itemsInBasket={itemsInBasket}
              userLabelSignedIn={
                userName
                  ? UiLocale.getString('component.my-account-menu.menu_welcome').replace(
                    '[NAME]',
                    `, ${userName}`
                  )
                  : ''
              }
              initialNavigation={initialNavigation}
              onSubmit={async (e, query) => {
                e.preventDefault();

                // Gathering URL to redirect after searching in Header input
                const redirectUrl = await fetchSearchListingPageURLBasedOnSearchQuery(query);

                log.info(`Redirecting to ${redirectUrl}`);
                window.location.href = redirectUrl;
              }}
              useOnlyInitialNavigation
            />
          )}
          {
          /* there were 404 error when loading on server */
          wasErrorOnServer && errorCode === 404 && (
            <Error404 />
          )
        }
          { /* there were a different error (probably 500) when loading on server */
          wasErrorOnServer && (
            <Error500 />
          )
        }
          {
          /* no error on server so we can render listing page body */
          !wasErrorOnServer && (
            <ListingPageBody />
          )
        }
          {initialNavigation && <FooterContainer />}
        </ListingPageAppWrapper>
      </EnvironmentThemeProvider>
    </>
  );
};

ListingPageApp.propTypes = {
  wasErrorOnServer: PropTypes.bool,
  errorCode: PropTypes.oneOf([404, 500]),
  isUserLoggedIn: PropTypes.bool,
  itemsInBasket: PropTypes.number,
  userName: PropTypes.string,
  initialNavigation: PropTypes.shape({
    catalog: PropTypes.shape({
      id: PropTypes.number,
      tenant: PropTypes.string,
      type: PropTypes.string,
      name: PropTypes.string,
      version: PropTypes.string,
      nav: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object]))
    }),
    pages: PropTypes.shape({
      id: PropTypes.number,
      tenant: PropTypes.string,
      type: PropTypes.string,
      name: PropTypes.string,
      version: PropTypes.string,
      nav: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object]))
    })
  }),
  updateInfoAboutBrowser: PropTypes.func.isRequired,
  updatePageAfterChangingPageUsingBrowserHistory: PropTypes.func.isRequired,
  loadGroupcallAndSetGroupcallData: PropTypes.func.isRequired
};

ListingPageApp.defaultProps = {
  wasErrorOnServer: false,
  errorCode: undefined,
  isUserLoggedIn: false,
  itemsInBasket: 0,
  userName: '',
  initialNavigation: undefined
};

const mapStateToProps = (state) => {
  const isUserLoggedIn = state.configuration.user ? state.configuration.user.isLoggedIn : false;

  return {
    isUserLoggedIn: isUserLoggedIn,
    itemsInBasket: state.configuration.itemsInBasket,
    userName: isUserLoggedIn ? state.configuration.user.first_name : '',
    initialNavigation: state.configuration ? state.configuration.navigationData : undefined
  };
};

const mapDispatchToProps = (dispatch) => ({
  updateInfoAboutBrowser: () => {
    dispatch(updateBrowserInfo());
  },
  loadGroupcallAndSetGroupcallData: () => {
    dispatch(loadAndSetGroupcall());
  },
  updatePageAfterChangingPageUsingBrowserHistory: (event) => {
    dispatch(
      getPageDataAfterChangingPageUsingBrowserHistory(
        event.state.nextPageAttributes,
        event.state.currentActivePage,
        event.state.triggeredFiltersIds
      )
    );
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(ListingPageApp);
